import React, { createContext, useContext, useMemo, useState, useCallback, PropsWithChildren } from 'react';

type SupportNotification = {
  ticketId: string;
  senderName: string;
  preview: string;
  createdAt: string;
};

type SupportContextValue = {
  // Total unread agent replies across all tickets — drives the red count
  // badge on the header support (headset) icon.
  unreadCount: number;
  lastNotification: SupportNotification | null;
  // Per-ticket unread reply counts — drives each ticket row's badge in the
  // support panel. Keyed by ticketId.
  unreadByTicket: Record<string, number>;
  panelOpen: boolean;
  openPanel: () => void;
  closePanel: () => void;
  handleIncomingMessage: (data: SupportNotification) => void;
  markTicketRead: (ticketId: string) => void;
  clearUnread: () => void;
};

const SupportContext = createContext<SupportContextValue>({
  unreadCount: 0,
  lastNotification: null,
  unreadByTicket: {},
  panelOpen: false,
  openPanel: () => {},
  closePanel: () => {},
  handleIncomingMessage: () => {},
  markTicketRead: () => {},
  clearUnread: () => {},
});

export const useSupportContext = () => useContext(SupportContext);

export const SupportProvider = ({ children }: PropsWithChildren) => {
  const [lastNotification, setLastNotification] = useState<SupportNotification | null>(null);
  // Per-ticket unread reply counts. Kept here (not in SupportLanding) so the
  // state survives the landing list unmounting/remounting as the player
  // navigates into a ticket and back. The header badge shows the sum; each
  // ticket row shows its own entry. This tracks replies received while the app
  // is open (the backend has no per-player read state), which is also how the
  // previous session-only highlight behaved.
  const [unreadByTicket, setUnreadByTicket] = useState<Record<string, number>>({});
  const [panelOpen, setPanelOpen] = useState(false);

  // Header badge count = number of new replies the player hasn't read yet,
  // summed across tickets. Opening a ticket clears that ticket's entry
  // (markTicketRead), which decrements this automatically.
  const unreadCount = useMemo(
    () => Object.values(unreadByTicket).reduce((sum, n) => sum + n, 0),
    [unreadByTicket]
  );

  const openPanel = useCallback(() => setPanelOpen(true), []);
  const closePanel = useCallback(() => setPanelOpen(false), []);

  const handleIncomingMessage = useCallback((data: SupportNotification) => {
    setLastNotification(data);
    if (!data.ticketId) return;
    // Count this reply as unread for its ticket. If the player is actively
    // viewing this ticket, SupportChat immediately marks it read again (see
    // its lastNotification effect), so the count nets back to zero.
    setUnreadByTicket((prev) => ({
      ...prev,
      [data.ticketId]: (prev[data.ticketId] || 0) + 1,
    }));
  }, []);

  const markTicketRead = useCallback((ticketId: string) => {
    setUnreadByTicket((prev) => {
      if (!prev[ticketId]) return prev;
      const next = { ...prev };
      delete next[ticketId];
      return next;
    });
  }, []);

  const clearUnread = useCallback(() => setUnreadByTicket({}), []);

  return (
    <SupportContext.Provider value={{
      unreadCount,
      lastNotification,
      unreadByTicket,
      panelOpen,
      openPanel,
      closePanel,
      handleIncomingMessage,
      markTicketRead,
      clearUnread,
    }}>
      {children}
    </SupportContext.Provider>
  );
};
